import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Typography, Space, Avatar, Badge, Divider } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { 
  MenuOutlined,
  CloseOutlined
} from '@ant-design/icons';
import DynamicMenu from './DynamicMenu';
import './LandingHeader.css';

const { Title, Text } = Typography;

interface LandingHeaderProps {
  logoText?: string;
  logoImage?: string;
  className?: string;
}

const LandingHeader: React.FC<LandingHeaderProps> = ({
  logoText = "Aarit Special School & Therapy Centre",
  logoImage = "/src/assets/img/ffslogo.png",
  className = ""
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastScrollY = useRef(0);
  const isScrolling = useRef(false);

  // Debounced scroll handler to prevent flickering
  const handleScroll = useCallback(() => {
    if (isScrolling.current) return;
    
    isScrolling.current = true;
    
    // Use requestAnimationFrame for smooth updates
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      const shouldBeScrolled = currentScrollY > 50;
      
      // Only update state if the scroll state actually needs to change
      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
      }
      
      lastScrollY.current = currentScrollY;
      isScrolling.current = false;
    });
  }, [isScrolled]);

  // Throttled scroll handler with proper debouncing
  const throttledScrollHandler = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      handleScroll();
    }, 16); // 60fps = ~16ms
  }, [handleScroll]);

  useEffect(() => {
    // Use passive scroll listener for better performance
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, [throttledScrollHandler]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Navigation is now handled by DynamicMenu component

  return (
    <header className={`landing-header ${className} ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-content">
        <div className="logo">
          <div className="logo-container">
            {!isScrolled && (
              <Avatar 
                src={logoImage} 
                size={56} 
                className="logo-avatar"
                shape="circle"
              />
            )}
            <div className="logo-text-container">
              <Title level={isScrolled ? 2 : 1} className="logo-text">{logoText}</Title>
              {!isScrolled && (
                <Text className="logo-subtitle">Empowering Special Needs Education</Text>
              )}
            </div>
          </div>
        </div>
        
        <nav className={`nav-menu ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <DynamicMenu />
          
          <Button 
            type="text" 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            icon={isMobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
          />
        </nav>
      </div>
      
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <Title level={4}>Menu</Title>
              <Button 
                type="text" 
                icon={<CloseOutlined />}
                onClick={() => setIsMobileMenuOpen(false)}
              />
            </div>
            <Divider />
            <div className="mobile-menu-items">
              {/* Mobile menu items will be handled by DynamicMenu in the future */}
              <Link 
                to="/" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="mobile-menu-item"
              >
                <span className="mobile-menu-icon">🏠</span>
                <span className="mobile-menu-label">Home</span>
              </Link>
              <Link 
                to="/login" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="mobile-menu-item"
              >
                <span className="mobile-menu-icon">🔐</span>
                <span className="mobile-menu-label">Login</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default LandingHeader;
